import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { 
  Wifi, WifiOff, RefreshCw, Check, AlertTriangle, X,
  Loader2, ArrowRight
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useWebSocket } from "@/hooks/useWebSocket";
import { getPlatformName } from "@/components/dashboard/constants";

// Platform colors for visual feedback
const PLATFORM_COLORS = {
  weibo: "#E6162D",
  bilibili: "#00A1D6",
  xiaohongshu: "#FF2442",
  douyin: "#010101",
  youtube: "#FF0000",
  tiktok: "#69C9D0",
  instagram: "#E1306C",
  batch: "#8B5CF6",
};

/**
 * Real-time sync status component using WebSocket
 */
export const SyncStatusIndicator = ({ 
  onSyncComplete, 
  lang = "en",
  showToasts = true,
  className = ""
}) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [activeSyncs, setActiveSyncs] = useState([]);
  
  // Generate unique client ID
  const clientId = useMemo(() => `dashboard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, []);

  // Handle WebSocket messages
  const handleMessage = (message) => {
    const { type, platform, account_name, posts_synced, error, message: msg } = message;
    
    switch (type) {
      case "connection_established":
        console.log("[SyncStatus] WebSocket connected");
        break;
        
      case "sync_started":
        setSyncStatus({ type: "syncing", platform, account_name });
        setActiveSyncs(prev => [...prev.filter(s => s.platform !== platform), { platform, account_name, status: "syncing" }]);
        if (showToasts) {
          toast.info(`${getPlatformName(platform, lang)} 동기화 시작...`, {
            description: account_name,
            duration: 2000,
          });
        }
        break;
        
      case "sync_progress":
        setSyncStatus({ type: "syncing", platform, account_name, message: msg });
        break;
        
      case "sync_completed":
        setSyncStatus({ type: "completed", platform, account_name, posts_synced });
        setActiveSyncs(prev => prev.filter(s => s.platform !== platform));
        if (showToasts) {
          toast.success(`${getPlatformName(platform, lang)} 동기화 완료`, {
            description: `${posts_synced || 0}개 게시물 동기화됨`,
            duration: 3000,
          });
        }
        // Trigger data refresh callback
        onSyncComplete?.(platform);
        break;
        
      case "sync_error":
        setSyncStatus({ type: "error", platform, account_name, error });
        setActiveSyncs(prev => prev.filter(s => s.platform !== platform));
        if (showToasts) {
          toast.error(`${getPlatformName(platform, lang)} 동기화 실패`, {
            description: error,
            duration: 4000,
          });
        }
        break;
        
      default:
        break;
    }
  };

  const { isConnected, connectionStatus, usePolling, connect } = useWebSocket(
    clientId,
    handleMessage,
    true
  );

  // Clear sync status after completion
  useEffect(() => {
    if (syncStatus?.type === "completed" || syncStatus?.type === "error") {
      const timer = setTimeout(() => {
        setSyncStatus(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [syncStatus]);

  // Connection status indicator
  const connectionIcon = isConnected ? (
    <Wifi className="h-3.5 w-3.5 text-emerald-500" />
  ) : usePolling ? (
    <RefreshCw className="h-3.5 w-3.5 text-amber-500 animate-spin" style={{ animationDuration: '3s' }} />
  ) : connectionStatus === "connecting" || connectionStatus === "reconnecting" ? (
    <RefreshCw className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
  ) : (
    <WifiOff className="h-3.5 w-3.5 text-muted-foreground" />
  );

  const connectionText = {
    connected: "실시간 연결됨",
    connecting: "연결 중...",
    polling: "폴링 모드",
    reconnecting: "연결 중...",
    disconnected: "대기 중",
    error: "폴링 모드",
  }[connectionStatus];

  // Active sync display
  if (activeSyncs.length > 0) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 ${className}`}>
        <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
        <span className="text-xs font-medium text-primary">
          {activeSyncs.length === 1 
            ? `${getPlatformName(activeSyncs[0].platform, lang)} 동기화 중...`
            : `${activeSyncs.length}개 플랫폼 동기화 중...`
          }
        </span>
      </div>
    );
  }

  // Completed status display
  if (syncStatus?.type === "completed") {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 ${className}`}>
        <Check className="h-3.5 w-3.5 text-emerald-500" />
        <span className="text-xs font-medium text-emerald-500">
          {syncStatus.posts_synced}개 게시물 동기화 완료
        </span>
      </div>
    );
  }

  // Default connection status
  return (
    <div 
      className={`flex items-center gap-2 px-2.5 py-1 rounded-full bg-muted/50 border border-border/50 cursor-pointer hover:bg-muted transition-colors ${className}`}
      onClick={!isConnected ? connect : undefined}
      title={connectionText}
    >
      {connectionIcon}
      <span className="text-xs text-muted-foreground hidden sm:inline">
        {connectionText}
      </span>
    </div>
  );
};

/**
 * Detailed sync panel for settings page
 */
export const SyncStatusPanel = ({ lang = "en" }) => {
  const [syncHistory, setSyncHistory] = useState([]);
  const clientId = useMemo(() => `settings_${Date.now()}`, []);

  const handleMessage = (message) => {
    if (message.type === "pong" || message.type === "connection_established") return;
    
    setSyncHistory(prev => [
      {
        ...message,
        timestamp: new Date().toLocaleTimeString(),
      },
      ...prev.slice(0, 19), // Keep last 20 messages
    ]);
  };

  const { isConnected, connectionStatus, usePolling } = useWebSocket(clientId, handleMessage, true);

  const getStatusIcon = (type) => {
    switch (type) {
      case "sync_started":
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case "sync_completed":
        return <Check className="h-4 w-4 text-emerald-500" />;
      case "sync_error":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <ArrowRight className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusText = (item) => {
    switch (item.type) {
      case "sync_started":
        return `${getPlatformName(item.platform, lang)} 동기화 시작`;
      case "sync_completed":
        return `${getPlatformName(item.platform, lang)}: ${item.posts_synced || 0}개 게시물 동기화됨`;
      case "sync_error":
        return `${getPlatformName(item.platform, lang)}: ${item.error}`;
      default:
        return item.message || item.type;
    }
  };

  return (
    <div className="space-y-4" data-testid="sync-status-panel">
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Wifi className="h-4 w-4 text-emerald-500" />
          ) : usePolling ? (
            <RefreshCw className="h-4 w-4 text-amber-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium">
            {isConnected ? "WebSocket 연결됨" : usePolling ? "폴링 모드 (5초 간격)" : connectionStatus === "connecting" ? "연결 중..." : "연결 안됨"}
          </span>
        </div>
        <Badge 
          variant={isConnected || usePolling ? "default" : "secondary"} 
          className={isConnected ? "bg-emerald-500/20 text-emerald-500" : usePolling ? "bg-amber-500/20 text-amber-500" : ""}
        >
          {isConnected ? "LIVE" : usePolling ? "POLLING" : "OFFLINE"}
        </Badge>
      </div>

      {/* Sync History */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-muted-foreground">실시간 동기화 로그</h4>
        <div className="max-h-[200px] overflow-y-auto space-y-1 rounded-lg bg-muted/30 p-2">
          {syncHistory.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              동기화 활동이 여기에 표시됩니다
            </p>
          ) : (
            syncHistory.map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-muted/50"
              >
                {getStatusIcon(item.type)}
                <span className="text-muted-foreground min-w-[60px]">{item.timestamp}</span>
                <span className="flex-1 truncate">{getStatusText(item)}</span>
                <Badge 
                  variant="outline" 
                  className="text-[10px] capitalize"
                  style={{ borderColor: PLATFORM_COLORS[item.platform] + "50" }}
                >
                  {item.platform}
                </Badge>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncStatusIndicator;
